import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import Pagination from '../../components/Pagination';
import {
  COPY,
  EMPTY_GROUP_TRIP_FILTERS,
  GROUP_TRIP_STATUS_OPTIONS,
  PAGE_SIZE,
  PRICING_TYPE_OPTIONS,
  buildGroupTripPageParams,
  formatDateCell,
  formatGroupTripStatus,
  formatPricingType,
  groupTripStatusClassName,
} from './travelManageConstants';

const TABLE_COLUMN_COUNT = 9;

function DetailRow({ label, value, className = '' }) {
  return (
    <div className="product-detail-row">
      <span className="product-detail-row__label">{label}</span>
      <span className={`product-detail-row__value ${className}`.trim()}>{value ?? '—'}</span>
    </div>
  );
}

export default function GroupTripListPage() {
  const [filters, setFilters] = useState(EMPTY_GROUP_TRIP_FILTERS);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  async function load(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.travelGroupTripPageQuery(
        buildGroupTripPageParams(pageNo, nextFilters, PAGE_SIZE),
      );
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    load(1, filters);
  }

  function handleReset() {
    const reset = { ...EMPTY_GROUP_TRIP_FILTERS };
    setFilters(reset);
    setError('');
    load(1, reset);
  }

  async function openDetail(groupTripId) {
    setError('');
    setDetailLoading(true);
    try {
      const data = await adminApi.travelGroupTripDetail(groupTripId);
      setDetail(data);
    } catch (err) {
      setError(err.message || '載入詳情失敗');
    } finally {
      setDetailLoading(false);
    }
  }

  return (
    <div className="page-card">
      <div className="section-header">
        <h2>揪團管理</h2>
      </div>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="gtGroupTripId">揪團 ID</label>
          <input
            id="gtGroupTripId"
            value={filters.groupTripId}
            onChange={(e) => updateFilter('groupTripId', e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="gtHostMemberId">主辦用戶 ID</label>
          <input
            id="gtHostMemberId"
            value={filters.hostMemberId}
            onChange={(e) => updateFilter('hostMemberId', e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="gtRecruitmentStatus">{COPY.status}</label>
          <select
            id="gtRecruitmentStatus"
            value={filters.recruitmentStatus}
            onChange={(e) => updateFilter('recruitmentStatus', e.target.value)}
          >
            {GROUP_TRIP_STATUS_OPTIONS.map((opt) => (
              <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="gtPricingType">定價</label>
          <select
            id="gtPricingType"
            value={filters.pricingType}
            onChange={(e) => updateFilter('pricingType', e.target.value)}
          >
            {PRICING_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="gtCurrencyCode">幣別</label>
          <input
            id="gtCurrencyCode"
            value={filters.currencyCode}
            onChange={(e) => updateFilter('currencyCode', e.target.value)}
          />
        </div>
        <div className="filter-actions">
          <button type="submit" className="btn btn-primary">{COPY.search}</button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            {COPY.reset}
          </button>
        </div>
      </form>

      {loading ? <p>載入中…</p> : null}
      {error ? <div className="alert alert-error">{error}</div> : null}

      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th>揪團 ID</th>
              <th>主辦用戶 ID</th>
              <th>標題</th>
              <th>{COPY.status}</th>
              <th>定價</th>
              <th>幣別</th>
              <th>名額</th>
              <th>{COPY.createdAt}</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMN_COUNT} className="empty-cell">
                  {COPY.emptyList}
                </td>
              </tr>
            ) : (
              records.map((row) => (
                <tr key={row.groupTripId}>
                  <td>{row.groupTripId}</td>
                  <td>{row.hostMemberId}</td>
                  <td>{row.recruitmentTitle || '—'}</td>
                  <td className={groupTripStatusClassName(row.recruitmentStatus)}>
                    {formatGroupTripStatus(row.recruitmentStatus)}
                  </td>
                  <td>{formatPricingType(row.pricingType)}</td>
                  <td>{row.currencyCode || '—'}</td>
                  <td>{row.capacity ?? '—'}</td>
                  <td>{formatDateCell(row.createTime)}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-link"
                      onClick={() => openDetail(row.groupTripId)}
                    >
                      {COPY.view}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(p) => load(p, filters)} />

      {detail ? (
        <div className="admin-modal-overlay" role="presentation" onClick={() => setDetail(null)}>
          <div
            className="admin-modal admin-modal--wide"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="admin-modal__header">
              <h3>揪團詳情 #{detail.groupTripId}</h3>
              <button type="button" className="admin-modal__close" onClick={() => setDetail(null)}>
                ×
              </button>
            </div>
            <div className="admin-modal__body">
              {detailLoading ? <p>載入中…</p> : null}
              <div className="product-detail-grid">
                <DetailRow label="主辦用戶 ID" value={detail.hostMemberId} />
                <DetailRow
                  label={COPY.status}
                  value={formatGroupTripStatus(detail.recruitmentStatus)}
                  className={groupTripStatusClassName(detail.recruitmentStatus)}
                />
                <DetailRow
                  label="定價"
                  value={`${formatPricingType(detail.pricingType)} ${detail.displayPrice ?? ''} ${detail.currencyCode ?? ''}`.trim()}
                />
                <DetailRow label="ACTIVE 參與者" value={detail.activeParticipantCount ?? 0} />
                <DetailRow label="摘要" value={detail.summary || '—'} />
              </div>
            </div>
            <div className="admin-modal__footer">
              <button type="button" className="btn btn-secondary" onClick={() => setDetail(null)}>
                關閉
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
